import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { platformApi, type PlatformAuditRow } from '../../../lib/api/platform.api';
import { formatDateTime, type DateLike } from '../../../lib/i18n-format';

// Map known action keys to a chip color; unknown actions fall back to default.
const ACTION_COLORS: Record<string, 'info' | 'warning' | 'success' | 'default'> = {
  'tenant.impersonate.start': 'info',
  'tenant.impersonate.stop': 'default',
  'tenant.status.change': 'warning',
  'tenant.invite.resend': 'success',
};

function summarizeDetails(details: Record<string, unknown> | null): string {
  if (!details) return '';
  return Object.entries(details)
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(', ');
}

export function AuditTab() {
  const { t } = useTranslation('platform');
  const [events, setEvents] = useState<PlatformAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  /** El formato lo resuelve el helper desde i18n; memorizarlo acá volvía a fijar
   *  el idioma a mano. */
  const dateFmt = (v: DateLike) =>
    formatDateTime(v, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { events: list } = await platformApi.listAudit();
      setEvents(list);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('audit.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('audit.error')}
        </Alert>
      )}

      {events.length === 0 ? (
        <Typography color="text.secondary" py={4}>
          {t('audit.empty')}
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('audit.table.when')}</TableCell>
                <TableCell>{t('audit.table.action')}</TableCell>
                <TableCell>{t('audit.table.actor')}</TableCell>
                <TableCell>{t('audit.table.tenant')}</TableCell>
                <TableCell>{t('audit.table.details')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{e.at ? dateFmt(e.at) : '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={ACTION_COLORS[e.action] ?? 'default'}
                      label={t(`audit.actions.${e.action}`, { defaultValue: e.action })}
                    />
                  </TableCell>
                  <TableCell>{e.actorEmail}</TableCell>
                  <TableCell>{e.tenantName || e.tenantId}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {summarizeDetails(e.details)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
